import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { Pagination } from "semantic-ui-react";
import styled from "styled-components";
import { useAppSelector } from "../../app/hooks";
import { IQueryParams, IQueryState, ISortableHeader } from "../../app/interfaces/ISortableTable";
import { RootState } from "../../app/store";
import QueryBar from "./QueryBar";
import { Table } from "@procore/core-react";
import { useDesktopView } from "../../hooks/use-desktop-view";
import { ITrade } from "../../app/interfaces/ITrade";
import { ICostCode } from "../../app/interfaces/ICostCode";
import ColumnsPicker from "./ColumnsPicker";
/**
 * @property data: Array of items to be sorted and displayed
 * @property headers: List of table headers to display in table
 * @property queryState: Tracks which page number and search term were used to get existing data
 * @property renderDataRow: A function from the parent which renders a data row 
 * @property updateData: A function to update data in table with new page numbers and/or search term
 * @property downloadable: Corresponding api supports returning csv file for query
*/
interface ISortableTableProps {
    dataSelector: (state: RootState) => any[];
    headers: ISortableHeader[];
    queryState?: IQueryState;
    renderDataRow: (dataItem: any) => JSX.Element;
    renderDataCard: (dataItem: any) => JSX.Element;
    //TODO: once all consumers of this component have converted to use updateData, remove undefined as possibility    
    queryData?: (queryParams?: IQueryParams) => void;
    exportable?: boolean;
    trades?: ITrade[];
    costCodes?: ICostCode[];
    // New: where to render the Columns action (defaults to #page-title-actions)
    columnsControlPortal?: string | Element;
}

export type SortOrder = 'ascn' | 'desc';

/**
 * @returns a sortable table for the given data
 */
const SortableTable = ({ dataSelector, headers, queryState, renderDataCard, renderDataRow, queryData, exportable, trades, costCodes, columnsControlPortal }: ISortableTableProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const maybePage = searchParams.get('page');
    const maybeReverse = searchParams.get('reverse');
    const maybeSearch = searchParams.get('search');
    const maybeSort = searchParams.get('sort');
    const maybeTrade = searchParams.get('trade');
    const maybeCostCode = searchParams.get('cost_code');
    const maybeIncludeInactive = searchParams.get('include_inactive');
    const maybeColumns = searchParams.get('columns');

    const data = useAppSelector(dataSelector);
    const isDesktop: boolean = useDesktopView();

    // Column visibility state derived from URL (columns=key1,key2,...). Defaults to all.
    const allHeaderKeys = useMemo(() => headers.map(h => String(h.key)), [headers]);
    // Local source of truth for visible columns; URL is synced on popup close.
    const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>(allHeaderKeys);

    // Persist selection per-page + header signature
    const columnsStorageKey = useMemo(() => {
        const path = window.location.pathname;
        return `sr:columns:${path}:${allHeaderKeys.join('|')}`;
    }, [allHeaderKeys]);

    // Hydrate initial selection: URL > localStorage > all
    const hydratedRef = useRef(false);
    useEffect(() => {
        if (hydratedRef.current) return;
        const fromUrl = maybeColumns?.split(',').map(k => k.trim()).filter(Boolean) || [];
        const fromStorage = (localStorage.getItem(columnsStorageKey) || '')
            .split(',')
            .map(k => k.trim())
            .filter(Boolean);
        const candidate = (fromUrl.length ? fromUrl : fromStorage).filter(k => allHeaderKeys.includes(k));
        const next = candidate.length ? candidate : allHeaderKeys;
        setVisibleColumnKeys(next);
        if (fromUrl.length) {
            localStorage.setItem(columnsStorageKey, fromUrl.join(','));
        }
        hydratedRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnsStorageKey, allHeaderKeys]);

    // Sync from URL if user navigates back/forward changing ?columns elsewhere.
    useEffect(() => {
      if (!hydratedRef.current) return;

      // If the param is absent, don't force local state; user is using local selection.
      if (maybeColumns === null) return;

      const fromUrl = (maybeColumns || '')
        .split(',')
        .map(k => k.trim())
        .filter(Boolean);
      const filtered = fromUrl.filter(k => allHeaderKeys.includes(k));
      setVisibleColumnKeys(filtered.length ? filtered : allHeaderKeys);
    }, [maybeColumns, allHeaderKeys]);

    // Persist to localStorage whenever selection changes
    useEffect(() => {
        if (visibleColumnKeys.length === allHeaderKeys.length) {
            localStorage.removeItem(columnsStorageKey);
        } else {
            localStorage.setItem(columnsStorageKey, visibleColumnKeys.join(','));
        }
    }, [visibleColumnKeys, columnsStorageKey, allHeaderKeys]);

    // Visible headers and indices in the original order
    const visibleHeaders = useMemo(
        () => headers.filter(h => visibleColumnKeys.includes(String(h.key))),
        [headers, visibleColumnKeys]
    );
    const visibleIndices = useMemo(
        () => headers.map((h, i) => (visibleColumnKeys.includes(String(h.key)) ? i : -1)).filter(i => i >= 0),
        [headers, visibleColumnKeys]
    );

    // Clone a Table.BodyRow and keep only cells at visibleIndices
    const projectVisibleCells = (rowEl: JSX.Element) => {
        const childrenArray = React.Children.toArray(rowEl.props.children);
        const filteredChildren = visibleIndices.map(idx => childrenArray[idx]).filter(Boolean);
        return React.cloneElement(rowEl, rowEl.props, filteredChildren);
    };

    const toggleColumn = (key: string) => {
        setVisibleColumnKeys(prev => {
            const set = new Set(prev);
            if (set.has(key)) {
                if (set.size === 1) return prev; // ensure at least one column remains
                set.delete(key);
            } else {
                set.add(key);
            }
            return Array.from(set);
        });
    };
    const resetColumns = () => setVisibleColumnKeys(allHeaderKeys);

    // Write current selection to URL (called when popup closes)
    const syncColumnsQueryParam = () => {
        const next = new URLSearchParams(searchParams);
        if (visibleColumnKeys.length === allHeaderKeys.length) {
            next.delete('columns');
        } else {
            next.set('columns', visibleColumnKeys.join(','));
        }
        setSearchParams(next, { replace: true });
    };

    const submitSearch = () => {
        const maybePageNumber = maybePage ? parseInt(maybePage) : 1;

        const queryParams: IQueryParams = {
            page: !isNaN(maybePageNumber) ? maybePageNumber : 1,
            reverse: maybeReverse === 'true' ? true : undefined,
            search: maybeSearch ? maybeSearch : undefined,
            sort: maybeSort ? maybeSort : undefined,
            trade: maybeTrade ? maybeTrade : undefined,
            cost_code: maybeCostCode ? maybeCostCode : undefined
        };

        if (maybeIncludeInactive === 'true') {
            queryParams.include_inactive = true;
        }

        if (queryData) {
            queryData(queryParams)
        }
    };

    useEffect(() => {
        submitSearch();
    }, [maybePage, maybeReverse, maybeSearch, maybeSort, maybeTrade, maybeCostCode, maybeIncludeInactive])

    const updatePagination = (page: number | string | undefined) => {
        if (queryData && page !== undefined) {
            searchParams.set('page', page.toString());
            setSearchParams(searchParams);
        }
    };

    // Portal target: explicit prop -> #page-title-actions -> resolved after mount
    const [headerActionsEl, setHeaderActionsEl] = useState<Element | null>(null);
    useEffect(() => {
        const resolveTarget = (): Element | null => {
            if (typeof columnsControlPortal === 'string') {
                return document.querySelector(columnsControlPortal) as Element | null;
            }
            if (columnsControlPortal instanceof Element) {
                return columnsControlPortal;
            }
            return document.querySelector('#page-title-actions');
        };
        // First attempt after mount/prop change
        let cancelled = false;
        const assign = () => {
            if (cancelled) return;
            setHeaderActionsEl(resolveTarget());
        };
        assign();
        // One micro-retry on next frame in case header mounts later in the commit
        const raf = requestAnimationFrame(assign);
        return () => {
            cancelled = true;
            cancelAnimationFrame(raf);
        };
    }, [columnsControlPortal]);

    const Portal = ({ target, children }: { target: Element; children: React.ReactNode }) => createPortal(children, target);

    return (
        <>
            {
                queryData &&
                <QueryBar
                    sortOptions={headers.filter(h => h.sortable)}
                    queryState={queryState}
                    exportable={exportable}
                    trades={trades}
                    costCodes={costCodes}
                />
            }

            {isDesktop && (
                headerActionsEl
                    ? <Portal target={headerActionsEl}>
                        <ColumnsPicker
                          headers={headers}
                          visibleColumnKeys={visibleColumnKeys}
                          onToggle={toggleColumn}
                          onShowAll={resetColumns}
                          onClose={syncColumnsQueryParam}
                        />
                      </Portal>
                    : <StyledControlsBar>
                        <ColumnsPicker
                          headers={headers}
                          visibleColumnKeys={visibleColumnKeys}
                          onToggle={toggleColumn}
                          onShowAll={resetColumns}
                          onClose={syncColumnsQueryParam}
                        />
                      </StyledControlsBar>
            )}

            {
                isDesktop &&
                    <Table.Container>
                        <Table>
                            <Table.Header>
                                <Table.HeaderRow>
                                    {visibleHeaders.map(hdr => (
                                        <Table.HeaderCell key={hdr.key}>{hdr.text}</Table.HeaderCell>
                                    ))}
                                </Table.HeaderRow>
                            </Table.Header>
                            <Table.Body>
                                {data?.map(dataItem => {
                                    const row = renderDataRow(dataItem);
                                    return projectVisibleCells(row);
                                })}
                            </Table.Body>
                        </Table>
                    </Table.Container>
            }

            {
                !isDesktop &&
                    <>
                        { data?.map(dataItem => renderDataCard(dataItem))}
                    </>
            }

            {  (queryState?.totalPages && queryState.totalPages > 1 ) && 
                <Pagination 
                    activePage={queryState.page}
                    onPageChange={(e,{activePage}) => updatePagination(activePage)}
                    totalPages={queryState.totalPages}
                    floated='right'
                />
            }
        </>
    )
};

// Styled components for controls
const StyledControlsBar = styled('div')`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 8px 0 12px;
`;



export default SortableTable;
